import api from './api.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface BidAnalysisSelectionDTO {
  supplier_inquiry_line_id: string;
  supplier_quotation_line_id: string;
  selected?: boolean;
  negotiated_unit_price?: number;
  justification?: string;
}

export interface BidAnalysis {
  id: string;
  company_id: string;
  supplier_inquiry_id: string;
  department_id?: string | null;
  analysis_number: string;
  status: string;
  recommendation_notes?: string;
  approval_notes?: string;
  created_at?: string;
  supplierInquiry?: { id: string; inquiry_number: string; inquiry_date?: string; department_id?: string | null };
  /** Present when loaded via for-po-consolidation */
  supplier_name?: string;
  supplier_names?: string[];
  /** Present on the register list */
  quoted_supplier_names?: string[];
  awarded_supplier_names?: string[];
  items?: Array<{ item_code: string | null; item_description: string | null }>;
}

export interface BidAnalysisSelection {
  id: string;
  bid_analysis_id: string;
  supplier_inquiry_line_id: string;
  supplier_quotation_id: string;
  supplier_quotation_line_id: string;
  selected: boolean;
  negotiated_unit_price?: number;
  justification?: string;
  converted_quantity?: number;
  supplierQuotation?: {
    id: string;
    quotation_number?: string;
    supplier_id?: string;
    supplier?: { supplier_name: string; id?: string };
  };
  supplierQuotationLine?: { id: string; unit_price: number; quoted_quantity: number; line_number: number };
  supplierInquiryLine?: {
    id: string;
    line_number: number;
    item_code?: string;
    item_description?: string;
    project_id?: string | null;
    project?: { id: string; project_number?: string | null; project_name?: string | null };
  };
}

export interface ComparisonOption {
  quotationLineId: string;
  supplierQuotationId: string;
  supplierId: string;
  supplierName: string;
  unit_price: number;
  line_total: number;
  lead_time_days?: number;
  promised_delivery_date?: string;
  quality_rating?: number;
  delivery_rating?: number;
  /** Supplier's offered spec / deviations */
  offered_specifications?: string | null;
  brand_offered?: string | null;
  /** UOM quoted by the supplier; uom_mismatch when it differs from the RFQ line (warning only). */
  quoted_uom?: string | null;
  uom_mismatch?: boolean;
  /** Line total ÷ theoretical weight (information only; not used for the recommendation). */
  price_per_kg?: number | null;
}

export interface LineComparison {
  options: ComparisonOption[];
  recommendedQuotationLineId: string | null;
  recommendedReason: string;
}

export interface ComparisonWithRecommendations {
  inquiryLines: Array<{
    id: string;
    line_number: number;
    item_code?: string;
    item_description?: string;
    quantity: number;
    uom?: string;
    project_id?: string | null;
    project_name?: string | null;
    /** Required spec from the RFQ line */
    specifications?: string | null;
    theoretical_weight?: number | null;
  }>;
  lineComparisons: Record<string, LineComparison>;
}

export class BidAnalysisService {
  async list(params?: { supplier_inquiry_id?: string; status?: string }): Promise<BidAnalysis[]> {
    const q = new URLSearchParams();
    if (params?.supplier_inquiry_id) q.set('supplier_inquiry_id', params.supplier_inquiry_id);
    if (params?.status) q.set('status', params.status);
    const res = await api.get(`/procurement/bid-analyses?${q.toString()}`);
    const raw = unwrap<BidAnalysis[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  /** Approved bid analyses with remaining convertible lines (PO wizard step 1). */
  async listForPoConsolidation(): Promise<BidAnalysis[]> {
    const res = await api.get('/procurement/bid-analyses/for-po-consolidation');
    const raw = unwrap<BidAnalysis[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getById(id: string): Promise<BidAnalysis> {
    const res = await api.get(`/procurement/bid-analyses/${id}`);
    return unwrap<BidAnalysis>(res);
  }

  async getQuotationsForBidAnalysis(id: string): Promise<any[]> {
    const res = await api.get(`/procurement/bid-analyses/${id}/quotations`);
    const raw = unwrap<any[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async getComparisonWithRecommendations(bidAnalysisId: string): Promise<ComparisonWithRecommendations> {
    const res = await api.get(`/procurement/bid-analyses/${bidAnalysisId}/comparison`);
    return unwrap<ComparisonWithRecommendations>(res);
  }

  async getSelections(id: string): Promise<BidAnalysisSelection[]> {
    const res = await api.get(`/procurement/bid-analyses/${id}/selections`);
    const raw = unwrap<BidAnalysisSelection[]>(res);
    return Array.isArray(raw) ? raw : [];
  }

  async setSelection(bidAnalysisId: string, dto: BidAnalysisSelectionDTO): Promise<BidAnalysisSelection> {
    const res = await api.post(`/procurement/bid-analyses/${bidAnalysisId}/selections`, dto);
    return unwrap<BidAnalysisSelection>(res);
  }

  async create(supplier_inquiry_id: string, department_id?: string | null): Promise<BidAnalysis> {
    const res = await api.post('/procurement/bid-analyses', { supplier_inquiry_id, department_id });
    return unwrap<BidAnalysis>(res);
  }

  async updateStatus(id: string, status: string): Promise<BidAnalysis> {
    const res = await api.put(`/procurement/bid-analyses/${id}/status`, { status });
    return unwrap<BidAnalysis>(res);
  }

  async updateNotes(
    id: string,
    recommendation_notes?: string,
    approval_notes?: string,
    department_id?: string | null
  ): Promise<BidAnalysis> {
    const res = await api.put(`/procurement/bid-analyses/${id}/notes`, {
      recommendation_notes,
      approval_notes,
      department_id,
    });
    return unwrap<BidAnalysis>(res);
  }
}

export const bidAnalysisService = new BidAnalysisService();
